"use client";

import { AlertCircle, Sparkles } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import Markdown from "react-markdown";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Link } from "@/lib/i18n";
import * as m from "@/paraglide/messages";
import { getSummary } from "@/services/controllers/dashboard";
import { useAppSelector } from "@/services/hooks/redux.hooks";

const AlertCard = () => {
  const { user_id } = useAppSelector((state) => state.user);
  const [visible, setVisible] = useState<boolean>(true);

  if (!visible || !user_id) return null;
  return (
    <Alert className="shadow-sm fixed md:bottom-4 bottom-0 bg-red-500 md:max-w-[500px] w-full md:right-4 right-0 z-30 text-white">
      <span className="flex items-center">
        <AlertCircle className="h-5 w-5" />
        <AlertTitle className="ml-2">{m.attention()}</AlertTitle>
      </span>
      <AlertDescription className="ml-2 mt-2">
        {m.next_plain_florian_glow()}
      </AlertDescription>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link
          href="/settings/exchanges"
          className={buttonVariants({ variant: "default", size: "sm" })}
          prefetch={false}
        >
          {m.update_key()}
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setVisible(false)}
          className="text-primary dark:text-white"
        >
          {m.dismiss()}
        </Button>
      </div>
    </Alert>
  );
};

const AICard = () => {
  const { selectedExchange, connectedKeys, fetched } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { subscription } = useAppSelector((state) => state.user);

  const [visible, setVisible] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [date, setDate] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (fetched && subscription != null && subscription !== 0 && subscription) {
      setVisible(true);
      getSummary({
        signal: controller.signal,
        params: {
          exchange_key_id: selectedExchange?.exchange_key_id,
        },
      }).then((response) => {
        setMessage(response.data.data.summary);
        setDate(response.data.data.generated_at);
        setLoading(false);
      });
    } else {
      setLoading(false);
      setVisible(false);
    }

    return () => {
      controller.abort();
    };
  }, [selectedExchange?.exchange_key_id, fetched, subscription]);

  if (!visible || connectedKeys.length <= 0) return null;

  if (loading) {
    return <></>;
  }

  return (
    <>
      <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-4 border border-purple-100/80 dark:border-purple-800 shadow-md">
        <div className="flex gap-3 items-start">
          <div className="flex-shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <h2 className="font-semibold text-purple-900 dark:text-purple-100 mb-1 flex md:flex-row flex-col md:justify-normal justify-center md:items-center items-left md:gap-1">
                {m.summary()}{" "}
                {date && (
                  <span className="italic text-xs font-medium text-purple-600 dark:text-purple-400">
                    {m.generated()} {moment(new Date(date).getTime()).fromNow()}
                  </span>
                )}
              </h2>
              <button
                onClick={() => setVisible(false)}
                className="text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
              >
                {m.dismiss()}
              </button>
            </div>
            <div className="text-purple-800 dark:text-purple-200 text-sm leading-relaxed">
              <Markdown>{message}</Markdown>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SAAICard = () => {
  const { selectedExchange, connectedKeys, fetched } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { subscription } = useAppSelector((state) => state.user);

  const [visible, setVisible] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [date, setDate] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (fetched && subscription != null && subscription !== 0 && subscription) {
      setVisible(true);
      getSummary(
        {
          signal: controller.signal,
          params: {
            exchange_key_id: selectedExchange?.exchange_key_id,
          },
        },
        "SA",
      ).then((response) => {
        setMessage(response.data.data.summary);
        setDate(response.data.data.generated_at);
        setLoading(false);
      });
    } else {
      setLoading(false);
      setVisible(false);
    }

    return () => {
      controller.abort();
    };
  }, [selectedExchange?.exchange_key_id, fetched, subscription]);

  if (!visible || connectedKeys.length <= 0) return null;

  if (loading) {
    return <></>;
  }

  return (
    <div className="bg-purple-50 dark:bg-purple-300/80 rounded-lg p-4 shadow-md border border-purple-100/80 dark:border-purple-300/80">
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0 mt-0.5">
          <Sparkles className="w-5 h-5 text-purple-600 dark:text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h2 className="font-semibold text-purple-900 dark:text-white mb-1 flex md:flex-row flex-col md:justify-normal justify-center md:items-center items-left md:gap-1">
              {m.SA_summary()}{" "}
              {date && (
                <span className="italic text-xs font-medium">
                  {m.generated()} {moment(new Date(date).getTime()).fromNow()}
                </span>
              )}
            </h2>
            <button
              onClick={() => setVisible(false)}
              className="text-sm text-purple-600 dark:text-white hover:text-purple-800 transition-colors"
            >
              {m.dismiss()}
            </button>
          </div>
          <p className="text-purple-800 dark:text-white text-sm leading-relaxed">
            <Markdown>{message}</Markdown>
          </p>
        </div>
      </div>
    </div>
  );
};

const MarketAICard = () => {
  const { selectedExchange, connectedKeys, fetched } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { user_id, subscription } = useAppSelector((state) => state.user);

  const [visible, setVisible] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [date, setDate] = useState<string>("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (fetched && subscription != null && subscription !== 0 && subscription) {
      setVisible(true);
      getSummary(
        {
          signal: controller.signal,
          params: {
            exchange_key_id: selectedExchange?.exchange_key_id,
          },
        },
        "Market",
      ).then((response) => {
        setMessage(response.data.data.summary);
        setDate(response.data.data.generated_at);
        setLoading(false);
      });
    } else {
      setLoading(false);
      setVisible(false);
    }

    return () => {
      controller.abort();
    };
  }, [selectedExchange?.exchange_key_id, fetched, subscription]);

  if (!visible || connectedKeys.length <= 0) return null;

  if (loading) {
    return <></>;
  }

  if (!user_id) return <></>;

  return (
    <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-4 shadow-md border border-purple-100/80 dark:border-purple-800 md:w-1/2 w-full self-center">
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0 mt-0.5">
          <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h2 className="font-semibold text-purple-900 dark:text-purple-100 mb-1 flex md:flex-row flex-col md:justify-normal justify-center md:items-center items-left md:gap-1">
              {m.market_summary()}{" "}
              {date && (
                <span className="italic text-xs font-medium dark:text-purple-400">
                  {m.generated()} {moment(new Date(date).getTime()).fromNow()}
                </span>
              )}
            </h2>
            <button
              onClick={() => setVisible(false)}
              className="text-sm text-purple-600 hover:text-purple-800 transition-colors dark:text-purple-400 dark:hover:text-purple-300"
            >
              {m.dismiss()}
            </button>
          </div>
          <p className="text-purple-800 text-sm leading-relaxed dark:text-purple-200">
            <Markdown>{message}</Markdown>
          </p>
        </div>
      </div>
    </div>
  );
};

export { AlertCard, AICard, SAAICard, MarketAICard };
